import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import { Node, mergeAttributes } from "@tiptap/core";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Quote,
  Minus,
  ImageIcon,
  FileVideo,
  Loader2,
} from "lucide-react";
import { uploadBigFileToS3 } from "../../services/uploads";

// Custom Video node for uploaded videos (not YouTube)
const VideoNode = Node.create({
  name: "video",
  group: "block",
  atom: true,

  addAttributes() {
    return {
      src: { default: null },
      controls: { default: true },
    };
  },

  parseHTML() {
    return [{ tag: "video" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["video", mergeAttributes(HTMLAttributes, {
      controls: true,
      class: "tw-max-w-full tw-rounded-lg tw-my-2",
      style: "max-height: 400px",
    })];
  },

  addCommands() {
    return {
      setVideo: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

const ToolbarButton = ({ onClick, isActive, children, title, disabled }) => (
  <div className="tw-relative tw-group">
    <button
      type="button"
      // Keep the caret in the editor: a button that takes focus makes the next
      // keystrokes land nowhere until TipTap refocuses on the next frame.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      aria-label={title}
      className={`tw-rounded tw-p-1.5 tw-transition-colors disabled:tw-opacity-40 ${
        isActive
          ? "tw-bg-primary-main/10 tw-text-primary-main"
          : "tw-text-gray-500 hover:tw-bg-gray-100 hover:tw-text-gray-700"
      }`}
    >
      {children}
    </button>
    {title && (
      <div className="tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-full tw-z-50 tw-mt-1.5 tw--translate-x-1/2 tw-whitespace-nowrap tw-rounded tw-bg-gray-800 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-text-white tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100">
        {title}
        <div className="tw-absolute tw-bottom-full tw-left-1/2 tw--translate-x-1/2 tw-border-4 tw-border-transparent tw-border-b-gray-800" />
      </div>
    )}
  </div>
);

const ToolbarDivider = () => <div className="tw-mx-1 tw-h-5 tw-w-px tw-bg-gray-200" />;

// Optional props (defaults keep the course editor unchanged):
// - media: false drops image/video upload (buttons and nodes) for text-only
//   fields such as club event descriptions.
// - className / contentClassName: extra classes on the frame / editable area.
// - autoFocus: put the caret at the end once mounted.
// `value` is only read on mount; remount (key) to load different content.
const RichTextEditor = ({ value, onChange, placeholder = "Start writing...", media = true, className = "", contentClassName = "", autoFocus = false }) => {
  const { t } = useTranslation();
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [uploading, setUploading] = useState(null); // "image" | "video" | null
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Placeholder.configure({ placeholder }),
      ...(media
        ? [
            Image.configure({
              inline: false,
              allowBase64: false,
              HTMLAttributes: {
                class: "tw-max-w-full tw-rounded-lg tw-my-2",
              },
            }),
            VideoNode,
          ]
        : []),
    ],
    autofocus: autoFocus ? "end" : false,
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        class:
          "tw-prose tw-prose-sm tw-max-w-none tw-px-3 tw-py-2 tw-min-h-[120px] tw-outline-none " +
          "[&_p]:tw-my-1 [&_ul]:tw-my-1 [&_ol]:tw-my-1 [&_h2]:tw-text-lg [&_h2]:tw-font-semibold [&_h2]:tw-mt-3 [&_h2]:tw-mb-1 " +
          "[&_h3]:tw-text-base [&_h3]:tw-font-semibold [&_h3]:tw-mt-2 [&_h3]:tw-mb-1 " +
          "[&_blockquote]:tw-border-l-2 [&_blockquote]:tw-border-gray-300 [&_blockquote]:tw-pl-3 [&_blockquote]:tw-italic [&_blockquote]:tw-text-gray-500 " +
          "[&_ul]:tw-list-disc [&_ul]:tw-pl-5 [&_ol]:tw-list-decimal [&_ol]:tw-pl-5 " +
          "[&_hr]:tw-my-3 [&_hr]:tw-border-gray-200 " +
          "[&_img]:tw-max-w-full [&_img]:tw-rounded-lg [&_img]:tw-my-2 " +
          "[&_video]:tw-max-w-full [&_video]:tw-rounded-lg [&_video]:tw-my-2 " +
          "[&_iframe]:tw-w-full [&_iframe]:tw-rounded-lg [&_iframe]:tw-my-2 " +
          "[&_.is-editor-empty:first-child::before]:tw-text-gray-400 [&_.is-editor-empty:first-child::before]:tw-float-left [&_.is-editor-empty:first-child::before]:tw-content-[attr(data-placeholder)] [&_.is-editor-empty:first-child::before]:tw-pointer-events-none [&_.is-editor-empty:first-child::before]:tw-h-0" +
          (contentClassName ? ` ${contentClassName}` : ""),
      },
    },
  });

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    setUploading("image");
    try {
      const url = await uploadBigFileToS3(file);
      editor.chain().focus().setImage({ src: url }).run();
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setUploading(null);
    }
    if (imageInputRef.current) imageInputRef.current.value = "";
  }, [editor]);

  const handleVideoUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    setUploading("video");
    try {
      const url = await uploadBigFileToS3(file);
      editor.chain().focus().setVideo({ src: url }).run();
    } catch (err) {
      console.error("Video upload failed:", err);
    } finally {
      setUploading(null);
    }
    if (videoInputRef.current) videoInputRef.current.value = "";
  }, [editor]);

  if (!editor) return null;

  return (
    <div className={"tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white focus-within:tw-border-primary-main focus-within:tw-ring-1 focus-within:tw-ring-primary-main/20" + (className ? ` ${className}` : "")}>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-0.5 tw-border-b tw-border-gray-200 tw-px-2 tw-py-1.5">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive("bold")}
          title={t("rich_text_bold", { defaultValue: "Bold" })}
        >
          <Bold className="tw-h-4 tw-w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive("italic")}
          title={t("rich_text_italic", { defaultValue: "Italic" })}
        >
          <Italic className="tw-h-4 tw-w-4" />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive("heading", { level: 2 })}
          title={t("rich_text_heading", { defaultValue: "Heading" })}
        >
          <Heading2 className="tw-h-4 tw-w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive("heading", { level: 3 })}
          title={t("rich_text_subheading", { defaultValue: "Subheading" })}
        >
          <Heading3 className="tw-h-4 tw-w-4" />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive("bulletList")}
          title={t("rich_text_bullet_list", { defaultValue: "Bullet list" })}
        >
          <List className="tw-h-4 tw-w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive("orderedList")}
          title={t("rich_text_numbered_list", { defaultValue: "Numbered list" })}
        >
          <ListOrdered className="tw-h-4 tw-w-4" />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          isActive={editor.isActive("blockquote")}
          title={t("rich_text_quote", { defaultValue: "Quote" })}
        >
          <Quote className="tw-h-4 tw-w-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title={t("rich_text_divider", { defaultValue: "Divider" })}
        >
          <Minus className="tw-h-4 tw-w-4" />
        </ToolbarButton>

        {media && (
          <>
            <ToolbarDivider />

            <ToolbarButton
              onClick={() => imageInputRef.current?.click()}
              title={t("rich_text_insert_image", { defaultValue: "Insert image" })}
              disabled={uploading === "image"}
            >
              {uploading === "image" ? (
                <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" />
              ) : (
                <ImageIcon className="tw-h-4 tw-w-4" />
              )}
            </ToolbarButton>
            <ToolbarButton
              onClick={() => videoInputRef.current?.click()}
              title={t("rich_text_upload_video", { defaultValue: "Upload video" })}
              disabled={uploading === "video"}
            >
              {uploading === "video" ? (
                <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" />
              ) : (
                <FileVideo className="tw-h-4 tw-w-4" />
              )}
            </ToolbarButton>
          </>
        )}
      </div>

      <EditorContent editor={editor} />

      {media && (
        <>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="tw-hidden"
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            onChange={handleVideoUpload}
            className="tw-hidden"
          />
        </>
      )}
    </div>
  );
};

export default RichTextEditor;
